import { createContext, useContext, useEffect, useState, useCallback } from "react";
import { getAttribution } from "@/lib/analytics";
import api from "@/lib/api";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null); // null = checking, false = logged out
  const [company, setCompany] = useState(null);
  const [settings, setSettings] = useState(null);

  const loadCompany = useCallback(async () => {
    try {
      const { data } = await api.get("/company");
      setCompany(data.company);
      setSettings(data.settings);
      return data;
    } catch {
      return null;
    }
  }, []);

  const refreshMe = useCallback(async () => {
    try {
      const { data } = await api.get("/auth/me");
      setUser(data);
      await loadCompany();
      return data;
    } catch {
      setUser(false);
      return null;
    }
  }, [loadCompany]);

  useEffect(() => {
    if (window.location.hash && window.location.hash.includes("session_id=")) {
      return; // AuthCallback will establish the session first
    }
    let settled = false;
    // Safety net: never leave the app stuck on the checking spinner.
    const fallback = setTimeout(() => { if (!settled) setUser((u) => (u === null ? false : u)); }, 8000);
    refreshMe().finally(() => { settled = true; clearTimeout(fallback); });
    return () => clearTimeout(fallback);
  }, [refreshMe]);

  const login = async (email, password) => {
    const { data } = await api.post("/auth/login", { email, password });
    setUser(data);
    await loadCompany();
    return data;
  };

  const register = async (name, email, password, accept_terms = false, wa = {}) => {
    const { data } = await api.post("/auth/register", { name, email, password, accept_terms, ...wa, attribution: getAttribution() });
    setUser(data);
    await loadCompany();
    return data;
  };

  const logout = async () => {
    try { await api.post("/auth/logout"); } catch {}
    setUser(false);
    setCompany(null);
    setSettings(null);
  };

  const loginWithGoogle = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/app";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const loginWithApple = () => {
    // Apple uses the same Emergent-managed OAuth entry point
    const redirectUrl = window.location.origin + "/app";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    <AuthContext.Provider value={{ user, company, settings, login, register, logout, loginWithGoogle, loginWithApple, refreshMe, loadCompany, setCompany, setSettings }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
